<template>
  <div ref="parent">
    <a-select
        label-in-value
        v-model:value="value"
        style="width: 100%"
        mode="multiple"
        :filter-option="false"
        :showSearch="true"
        @search="search"
        v-model:open="open"
        @focus="search('', true)"
        @select="search('', true)"
        :options="list.data.list"
        :allowClear="true"
        :placeholder="'选择' + title"
        ref="select"
    >
      <template #dropdownRender="{ menuNode: menu }">
        <v-nodes :vnodes="menu"/>
        <template v-if="createHandler || list.data.count > list.data.pageSize">
          <a-divider style="margin: 4px 0"/>
          <div class="drop-menu" @mousedown.stop.prevent>
              <sc-btn
                  type="link"
                  text="创建"
                  :handler="createHandler"
                  v-if="createHandler"
              />
              <a-pagination
                  simple
                  hide-on-single-page
                  responsive
                  show-less-items
                  show-quick-jumper
                  v-model:current="list.data.pageNum"
                  :page-size="list.data.pageSize"
                  :total="list.data.count"
              />
          </div>
        </template>

      </template>
    </a-select>
  </div>
</template>

<script setup lang="ts">
import {computed, defineProps, nextTick, reactive, ref, watchEffect} from 'vue';

import {windows} from '@/components/common/window/Windows';
import {PageList} from '@/models/PageList';
import {SelectRemoteFieldType} from "@/components/business/crud/interface/FieldType";
import {Select} from "ant-design-vue";
import _ from "lodash";


interface Option {
  label: string;
  value: string;
}


const props = defineProps<{
  title: string;
  widget: SelectRemoteFieldType;
  modelValue?: Option[];
}>();

const emit = defineEmits<{
  (event: "update:modelValue", data: unknown): void;
}>();

/**
 * 获取搜索方法
 * @param cb
 */
function useSearch() {
  // 之前的关键字
  const loading = ref(false);
  const keyword = ref("");

  // 关键字搜索
  async function search(kw: string) {
    keyword.value = kw;
    loading.value = false;
  }

  const debounceSearch = _.debounce(search, 800);
  const finalSearch = async (kw: string, flush = false) => {
    loading.value = true;
    await debounceSearch(kw);
    if (flush) {
      debounceSearch.flush();
    }
  };
  return [finalSearch, keyword, loading] as const;
}

const dataParser = (item: Record<string, any>) => {
  return {
    label: item[props.widget.labelFieldName],
    value: item[props.widget.valueFieldName],
  };
};

const value = computed({
  get: () => {
    return props.modelValue instanceof Array ? props.modelValue.map(dataParser) : [];
  },
  set: v => {
    if (v instanceof Array && v.length > 0) {
      emit("update:modelValue", v.map(i => ({
        [props.widget.labelFieldName]: i.label,
        [props.widget.valueFieldName]: i.value,
      })));
    } else {
      emit("update:modelValue", []);
    }
  }
})

function VNodes(_: any, {attrs}: any) {
  return attrs.vnodes;
}

const [search, kw] = useSearch();
const condition = computed(() => {
  const value = {
    $and: [] as any
  };
  if (kw.value) {
    value.$and.push({
      [props.widget.labelFieldName]: {$like: '%' + kw.value + '%'}
    })
  }
  if (props.widget.condition) {
    value.$and.push(props.widget.condition)
  }
  return value;
})
const config = reactive({
  path: props.widget.path,
  pageSize: 8,
  condition: condition.value,
  load: [props.widget.labelFieldName, props.widget.valueFieldName],
  parser: async (origin: any[]) => {
    return origin.map(dataParser);
  },
})
// 远程数据
const list = new PageList<Option>(config);
watchEffect(() => {
  config.condition = condition.value;
})
list.init();


const open: any = ref();
// 选项为空创建
let createHandler: (() => any) | undefined;
if (props.widget.createPagePath !== undefined) {
  createHandler = async () => {
    if (props.widget.createPagePath !== undefined) {
      open.value = false;
      await nextTick(() => {
        open.value = undefined;
      });
      select.value.blur();
      // 打开创建页面，等待创建完成
      const id = windows.open({
        content: {
          type: 'route',
          href: props.widget.createPagePath,
        },
        title: '创建' + props.title,
      });
      const res = await windows.getCloseData(id);
      if (res) {
        value.value = [...value.value, {
          label: res[props.widget.labelFieldName],
          value: res[props.widget.valueFieldName],
        }]
        await list.refresh();
      }
    }
  };
}

const select = ref<InstanceType<typeof Select>>();

</script>

<style lang="scss" scoped>
.notfound {
  display: flex;
  flex-direction: row;
  //justify-content: center;
  align-items: center;
  height: 60px;
}

.drop-menu {
  padding: $spacing-small;
  cursor: pointer;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
}
</style>
